import { useState } from "react";
import {
  Drawer,
  Button,
  Table,
  Typography,
  message,
  Spin,
  Alert,
  Space,
  Popconfirm,
  Form,
  Input,
  InputNumber,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_FORM_WIDTH,
} from "../../../../components";
import {
  useSystemPackagesQuery,
  useCreateSystemPackageMutation,
  useUpdateSystemPackageMutation,
  useDeleteSystemPackageMutation,
} from "../../../../Services";
import { PackageFeatureManager } from "./PackageFeatureManager";

const { Text } = Typography;

interface PackageItem {
  id: string;
  name: string;
  price: number;
  note: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = "list" | "form" | "features";

export const PackageManageDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [mode, setMode] = useState<Mode>("list");
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState<PackageItem | null>(null);
  const [featurePackage, setFeaturePackage] = useState<PackageItem | null>(
    null,
  );
  const [form] = Form.useForm();

  const {
    data: packages = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useSystemPackagesQuery();

  const createMutation = useCreateSystemPackageMutation();
  const updateMutation = useUpdateSystemPackageMutation();
  const deleteMutation = useDeleteSystemPackageMutation();

  const resetViews = () => {
    setMode("list");
    setIsEditing(false);
    setEditingItem(null);
    setFeaturePackage(null);
    form.resetFields();
  };

  const handleClose = () => {
    resetViews();
    onClose();
  };

  const handleCreate = () => {
    setIsEditing(false);
    setEditingItem(null);
    setMode("form");
    form.resetFields();
    form.setFieldsValue({ price: 0 });
  };

  const handleEdit = (item: PackageItem) => {
    setIsEditing(true);
    setEditingItem(item);
    setMode("form");
    form.setFieldsValue({
      name: item.name,
      price: item.price,
      note: item.note ?? "",
    });
  };

  const handleManageFeatures = (item: PackageItem) => {
    setFeaturePackage(item);
    setMode("features");
  };

  const handleCancelForm = () => {
    setMode("list");
    setIsEditing(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSave = async (values: any) => {
    try {
      if (isEditing && editingItem) {
        const res = await updateMutation.mutateAsync({
          id: editingItem.id,
          name: values.name,
          price: values.price ?? 0,
          note: values.note ?? "",
        });
        if (res?.success) {
          message.success(res.message || "Package updated successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to update package.");
        }
      } else {
        const res = await createMutation.mutateAsync({
          name: values.name,
          price: values.price ?? 0,
          note: values.note ?? "",
        });
        if (res?.success) {
          message.success(res.message || "Package created successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to create package.");
        }
      }
    } catch (err: any) {
      message.error(err.message || "Error saving package.");
    }
  };

  const handleDelete = async (item: PackageItem) => {
    try {
      const res = await deleteMutation.mutateAsync(item.id);
      if (res?.success) {
        message.success(res.message || "Package removed.");
      } else {
        throw new Error(res?.message || "Failed to delete package.");
      }
    } catch (err: any) {
      message.error(err.message || "Error deleting package.");
    }
  };

  const columns = [
    {
      title: "Package",
      dataIndex: "name",
      key: "name",
      render: (text: string, item: PackageItem) => (
        <div className="flex flex-col">
          <Text code className="text-xs font-semibold">
            {text}
          </Text>
          {item.note ? (
            <Text type="secondary" className="text-[11px]">
              {item.note}
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 110,
      render: (v: number) =>
        typeof v === "number"
          ? v.toLocaleString(undefined, { minimumFractionDigits: 2 })
          : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: any, item: PackageItem) => (
        <Space size="small">
          <Button
            type="text"
            icon={<AppstoreOutlined className="text-green-600" />}
            size="small"
            onClick={() => handleManageFeatures(item)}
            title="Manage features"
          />
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            size="small"
            onClick={() => handleEdit(item)}
          />
          <Popconfirm
            title="Remove this package?"
            description="This will deactivate the package for all tenants."
            onConfirm={() => handleDelete(item)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const errorMsg = isError
    ? (error as any)?.message || "Failed to load packages."
    : null;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={DRAWER_FORM_WIDTH}
      title={
        mode === "features" && featurePackage
          ? drawerTitle(
              `Features · ${featurePackage.name}`,
              "Enable or disable sub-features included in this package",
            )
          : drawerTitle(
              "System Packages",
              "Manage feature bundles by name, price and note",
            )
      }
      destroyOnClose
      footer={drawerFooter({
        actions: [{ label: "Close", onClick: handleClose }],
      })}
    >
      <div className="flex flex-col gap-4 h-full">
        {mode === "features" && (
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => setMode("list")}
            className="w-fit"
          >
            Back to packages
          </Button>
        )}

        {errorMsg && mode === "list" && (
          <Alert
            message="Connection Error"
            description={
              <div className="flex flex-col gap-2">
                <p className="text-xs text-red-800">{errorMsg}</p>
                <Button
                  size="small"
                  type="primary"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  className="w-fit text-xs"
                >
                  Retry
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {mode === "list" && !errorMsg && (
          <>
            <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border">
              <Text className="text-xs text-gray-500">
                Packages bundle sub-features behind a single name, price and
                note.
              </Text>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size="small"
                className="!bg-black !border-black hover:!bg-gray-800 text-xs"
              >
                Add Package
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Spin tip="Loading packages..." size="large" />
              </div>
            ) : (
              <div className="flex-grow overflow-auto relative min-h-[200px]">
                {packages.length > 0 ? (
                  <Table
                    dataSource={packages}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    className="border rounded-lg"
                    locale={{ emptyText: "No packages found." }}
                  />
                ) : (
                  <div className="text-center text-gray-400 text-sm py-10">
                    No packages yet. Click "Add Package" to create one.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {mode === "form" && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-slate-800">
                {isEditing ? "Edit Package" : "New Package"}
              </span>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              size="middle"
            >
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Name is required." }]}
              >
                <Input placeholder="e.g. Starter Plan" className="text-xs" />
              </Form.Item>

              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: "Price is required." }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-full text-xs"
                  placeholder="0.00"
                />
              </Form.Item>

              <Form.Item name="note" label="Note">
                <Input.TextArea
                  rows={3}
                  placeholder="Optional notes"
                  className="text-xs"
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button size="small" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={
                      createMutation.isPending || updateMutation.isPending
                    }
                    size="small"
                    className="!bg-black !border-black hover:!bg-gray-800"
                  >
                    Save
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        {mode === "features" && featurePackage && (
          <PackageFeatureManager packageId={featurePackage.id} />
        )}
      </div>
    </Drawer>
  );
};
